// Custom file-upload UI for Gravity Forms file fields.
//
// GF's native <input type="file"> shows untranslatable browser chrome
// ("Choose File" / "No file chosen" — shadow-DOM, unreachable via gettext).
// The PHP filter lb_gf_fileupload_custom_ui injects translated strings as
// data-aw-choose / data-aw-empty on the wrapper; we read them here, hide the
// native input visually (keeping it functional for GF submit/validation), and
// render a branded trigger button + filename span instead.
//
// Re-init is idempotent (data-aw-fileupload-init guard). Works across GF AJAX
// re-renders by re-running on gform_post_render (jQuery) and observing new
// DOM nodes via MutationObserver.

function initFileUpload(container: HTMLElement): void {
  // Idempotency guard — skip if already initialised.
  if (container.dataset.awFileuploadInit) return;
  container.dataset.awFileuploadInit = "1";

  const choose = container.dataset.awChoose ?? "Choose file";
  const empty  = container.dataset.awEmpty  ?? "No file chosen";

  const input = container.querySelector<HTMLInputElement>('input[type="file"]');
  if (!input) return;

  // Visually hide the native input while keeping it accessible + clickable-by-proxy.
  // opacity:0 + absolute sizing ensures GF can still trigger validation on it and
  // programmatic .click() from the trigger still works. pointer-events:none prevents
  // accidental direct clicks that would double-open the picker in some browsers.
  input.classList.add("aw-file-native");

  // Build custom UI.
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "aw-file-trigger";
  trigger.textContent = choose;

  const nameSpan = document.createElement("span");
  nameSpan.className = "aw-file-name";
  nameSpan.textContent = empty;

  // Wrap trigger + name in a flex row, inserted before the native input so the
  // native input can stay in DOM flow at the end (required for some GF versions).
  const row = document.createElement("div");
  row.className = "aw-file-row";
  row.appendChild(trigger);
  row.appendChild(nameSpan);
  container.insertBefore(row, input);

  trigger.addEventListener("click", () => input.click());

  input.addEventListener("change", () => {
    const files = input.files;
    if (!files || files.length === 0) {
      nameSpan.textContent = empty;
      return;
    }
    nameSpan.textContent = Array.from(files)
      .map((f) => f.name)
      .join(", ");
  });
}

function initAll(): void {
  document
    .querySelectorAll<HTMLElement>("[data-aw-fileupload]:not([data-aw-fileupload-init])")
    .forEach(initFileUpload);
}

// Initial pass on DOMContentLoaded (or immediately if already ready).
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}

// Re-init after GF AJAX re-renders the form DOM.
// gform_post_render fires on jQuery(document) after every AJAX page turn / submit.
if (typeof window !== "undefined" && (window as any).jQuery) {
  (window as any).jQuery(document).on("gform_post_render", initAll);
}

// Belt-and-suspenders: MutationObserver catches any other dynamic insertions
// (e.g. conditional logic showing a hidden file field).
const observer = new MutationObserver(() => {
  const pending = document.querySelectorAll<HTMLElement>(
    "[data-aw-fileupload]:not([data-aw-fileupload-init])"
  );
  if (pending.length) initAll();
});

observer.observe(document.body, { childList: true, subtree: true });
